<template>
  <v-container class="pt-4 px-1" fluid>
    <h1 class="dash-campanhas-title">Histórico de campanhas</h1>
    <v-card>
      <v-card-text>
        <paginated-data-table
          ref="table"
          :headers.sync="headers"
          :query="query"
          :custom-items-per-page="10"
          query-name="genericCampaigns"
          query-main-property-name="campaigns"
          :query-variables="{ ...queryVariables, channels, campaignIds: getSelectedCampaignsIds }"
          :skip="skipQueries"
          :graphql-uri="$microservicesUrls['campaigns']"
          use-headers-settings
          export-all-result
          :export-all-result-incremental-page-size="10"
          use-export-csv
          headers-settings-name="general_campaign_results"
          csv-filename="resultados_gerais_de_campanhas"
          :get-csv-data-shape="getCsvDataShape"
          item-key="_id"
          no-data-text="Não existem dados a serem exibidos. Selecione outro período ou entre em contato com o suporte pelo chat."
        >
          <template v-slot:item.title="{ item }">
            <router-link class="link" :to="{ path: getCampaignRouteLink(item._id, item.mode, isMariaCampaign(item)) }" target="_blank">
              {{ item.title | hifen }}
            </router-link>
          </template>
          <template v-slot:item.description="{ item }">
            {{ item.description | hifen }}
          </template>
          <template v-slot:item.final_date="{ item }">
            {{ `${formatDate(item.initial_date)} - ${formatDate(item.final_date)}` }}
          </template>
          <template v-slot:item.campaign_type="{ item }">
            {{ getCampaignTypeLabel(item.campaign_type) }}
          </template>
          <template v-slot:item.metrics.clients_metrics.received.count="{ item }">
            {{ item.metrics.clients_metrics.received.count | localeString }}
            <mf-quick-actions
              v-if="item._id"
              :quick-action-group-id="`resultados-campanhas-atingidos-${item._id}`"
              :short-description="`Clientes que foram atingidos pela campanha ${item.title}`"
              :long-description="`Clientes que receberam disparos da campanha ${item.title}`"
              :segmentation-group="criteriaGroup([campaignReceivedCriteria([item._id])])"
            />
          </template>
          <template v-slot:item.metrics.clients_metrics.received_and_bought.count="{ item }">
            {{ item.metrics.clients_metrics.received_and_bought.count | localeString }}
            <mf-quick-actions
              v-if="item._id"
              :quick-action-group-id="`resultados-campanhas-compraram-${item._id}`"
              :short-description="`Clientes que compraram na campanha ${item.title}`"
              :long-description="`Clientes que receberam disparos e compraram na campanha ${item.title}`"
              :segmentation-group="criteriaGroup([campaignReceivedAndPurchasedCriteria([item._id])])"
            />
          </template>
          <template v-slot:item.metrics.clients_metrics.received_with_products.count="{ item }">
            {{ item.metrics.clients_metrics.received_with_products.count | localeString }}
            <mf-quick-actions
              v-if="item._id"
              :quick-action-group-id="`resultados-campanhas-compraram-divulgados-${item._id}`"
              :short-description="`Clientes que compraram produtos divulgados na campanha ${item.title}`"
              :long-description="`Clientes que receberam disparos e compraram produtos divulgados na campanha ${item.title}`"
              :segmentation-group="criteriaGroup([campaignReceivedAndPurchasedAnyProductCriteria([item._id])])"
            />
          </template>
          <template v-slot:item.delivery_rate_metrics="{ item }">
            <v-tooltip top :disabled="!isMariaCampaign(item)" max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{
                    getRateMetrics(item.metrics.general_metrics.general.received.messages, item.metrics.general_metrics.general.sent.messages) | localeString
                  }}%
                </span>
              </template>
              <span>Em campanhas que contém mais de um canal, a taxa de entrega é a média de entrega entre todos os canais utilizados na campanha. </span>
            </v-tooltip>
          </template>
          <template v-slot:item.metrics.analytic_metrics.price="{ item }"> R$ {{ item.metrics.analytic_metrics.price | localeString(2) }} </template>
          <template v-slot:item.metrics.analytic_metrics.credits="{ item }"> {{ item.metrics.analytic_metrics.credits | localeString }} </template>
          <template v-slot:item.metrics.general_metrics.general.sent.messages="{ item }">
            {{ item.metrics.general_metrics.general.sent.messages | localeString }}
          </template>
          <template v-slot:item.conversion_rate_metrics="{ item }">
            <v-tooltip top :disabled="!isMariaCampaign(item)" max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ getRateMetrics(item.metrics.clients_metrics.received_and_bought.count, item.metrics.clients_metrics.received.count) | localeString }}%
                </span>
              </template>
              <span>Em campanhas que contém mais de um canal, a taxa de conversão é a média de conversão entre todos os canais utilizados na campanha.</span>
            </v-tooltip>
          </template>
          <template v-slot:item.mode="{ item }">
            <span v-if="item.mode">{{ getCampaignModeDescription(item.mode) }}</span>

            <div v-else-if="isMariaCampaign(item)">
              <span v-if="getCampaingsLength(item) === 1">{{ getFormatedMariaCampaignChannels(item) }}</span>

              <v-tooltip v-else top>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">{{ getCampaingsLength(item) }} canais</span>
                </template>
                <span>{{ getFormatedMariaCampaignChannels(item) }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.metrics.clients_metrics.received_and_bought.revenue="{ item }">
            R$ {{ item.metrics.clients_metrics.received_and_bought.revenue | localeString(2) }}
          </template>
          <template v-slot:item.metrics.clients_metrics.received_and_bought.average_ticket="{ item }">
            R$ {{ item.metrics.clients_metrics.received_and_bought.average_ticket | localeString(2) }}
          </template>
          <template v-slot:item.metrics.analytic_metrics.received_roi="{ item }">
            {{ checkIfSomeCampaignModesHasCosts(item.mode) ? item.metrics.analytic_metrics.received_roi : '-' | localeString(2) }}
          </template>
          <template v-slot:item.metrics.clients_metrics.received_with_products.revenue="{ item }">
            R$ {{ item.metrics.clients_metrics.received_with_products.revenue | localeString(2) }}
          </template>
          <template v-slot:item.metrics.products_metrics.totals.received.revenue="{ item }">
            R$ {{ item.metrics.products_metrics.totals.received.revenue | localeString(2) }}
          </template>
          <template v-slot:item.metrics.products_metrics.totals.received.quantity="{ item }">
            {{ item.metrics.products_metrics.totals.received.quantity | localeString(2) }}
          </template>
          <template v-slot:item.metrics.products_metrics.totals.received.impact="{ item }">
            {{
              item.metrics.products_metrics.totals.received.quantity
                ? (item.metrics.products_metrics.totals.received.quantity / item.metrics.products_metrics.totals.total.quantity) * 100
                : 0 | localeString(2)
            }}%
          </template>
          <template v-slot:item.metrics.clients_metrics.received_with_only_products.count="{ item }">
            {{ item.metrics.clients_metrics.received_with_only_products.count | localeString }}
            <mf-quick-actions
              v-if="item._id"
              :quick-action-group-id="`resultados-campanhas-compraram-apenas-divulgados-${item._id}`"
              :short-description="`Clientes que compraram apenas produtos divulgados na campanha ${item.title}`"
              :long-description="`Clientes que receberam disparos e compraram apenas produtos divulgados na campanha ${item.title}`"
              :segmentation-group="criteriaGroup([campaignReceivedAndOnlyPurchasedAnyProductCriteria([item._id])])"
            />
          </template>
          <template v-slot:item.metrics.general_metrics.general.segments_quantity.messages="{ item }">
            {{ item.metrics.general_metrics.general.segments_quantity.messages | localeString }}
          </template>
          <template v-slot:item.metrics.general_metrics.general.read.messages="{ item }">
            {{ item.metrics.general_metrics.general.read.messages | localeString }}
          </template>
          <template v-slot:item.metrics.general_metrics.general.clicked.messages="{ item }">
            {{ item.metrics.general_metrics.general.clicked.messages | localeString }}
          </template>
          <template v-slot:item.metrics.general_metrics.general.senders_quantity.messages="{ item }">
            {{ item.metrics.general_metrics.general.senders_quantity.messages | localeString }}
          </template>
          <template v-slot:item.metrics.contacts_metrics.blacklisted_quantity="{ item }">
            {{ item.metrics.contacts_metrics.blacklisted_quantity | localeString }}
          </template>
          <template v-slot:item.products="{ item }">
            <mf-tooltip v-if="getSafe(() => item.products.length)" left>
              <template #activator="{ on }">
                {{ getSafe(() => item.products.length) || 0 }}
              </template>
              <div v-for="(product, index) in item.products" :key="`${item._id}-1-${index}`">
                <span v-if="product">
                  {{ product.name || '' }}
                </span>
                <br />
              </div>
            </mf-tooltip>
            <span v-else>0</span>
          </template>
          <template v-slot:item.units="{ item }">
            <mf-tooltip v-if="getSafe(() => item.units.length)" left>
              <template #activator="{ on }">
                {{ getSafe(() => item.units.length) || 0 }}
              </template>
              <div v-for="(unit, index) in item.units" :key="`${item._id}-1-${index}`">
                <span v-if="unit">
                  {{ unit.name || '' }}
                </span>
                <br />
              </div>
            </mf-tooltip>
            <span v-else>0</span>
          </template>
          <template v-slot:item.categories_level_1="{ item }">
            <mf-tooltip left>
              <template #activator="{ on }">
                {{ getCategories(item.products, 1).length }}
              </template>
              <div v-for="(category, index) in getCategories(item.products, 1)" :key="`${item._id}-1-${index}`">
                <span>
                  {{ category }}
                </span>
                <br />
              </div>
            </mf-tooltip>
          </template>
          <template v-slot:item.categories_level_2="{ item }">
            <mf-tooltip left>
              <template #activator="{ on }">
                {{ getCategories(item.products, 2).length }}
              </template>
              <div v-for="(category, index) in getCategories(item.products, 2)" :key="`${item._id}-1-${index}`">
                <span>
                  {{ category }}
                </span>
                <br />
              </div>
            </mf-tooltip>
          </template>
          <template v-slot:item.categories_level_3="{ item }">
            <mf-tooltip left>
              <template #activator="{ on }">
                {{ getCategories(item.products, 3).length }}
              </template>
              <div v-for="(category, index) in getCategories(item.products, 3)" :key="`${item._id}-1-${index}`">
                <span>
                  {{ category }}
                </span>
                <br />
              </div>
            </mf-tooltip>
          </template>
        </paginated-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { DateTime } from 'luxon'
import { QUERY_GET_CAMPAIGNS_RESULTS } from '@/modules/campanhas/resultados-gerais/graphql'
import {
  criteriaGroup,
  campaignReceivedCriteria,
  campaignReceivedAndPurchasedCriteria,
  campaignReceivedAndPurchasedAnyProductCriteria,
  campaignReceivedAndOnlyPurchasedAnyProductCriteria,
} from '@/components/quick-actions/segmentationGroups.js'
import { roundFloat } from '@/config/helpers'
import truncate from 'lodash/truncate'

export default {
  name: 'CampaignHistoryTable',
  components: {
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
  },
  props: {
    queryVariables: {
      type: Object,
      required: true,
    },
    channels: {
      type: Array,
      required: true,
    },
    getSelectedCampaignsIds: {
      type: Array,
      required: true,
    },
    skipQueries: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    query() {
      return QUERY_GET_CAMPAIGNS_RESULTS
    },
    headers() {
      const headers = [
        {
          text: 'Campanha',
          value: 'title',
          width: '306px',
          isRequired: true,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '306px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Período de mensuração',
          value: 'final_date',
          width: '250px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Tipo de campanha',
          value: 'campaign_type',
          width: '200px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Atingidos',
          value: 'metrics.clients_metrics.received.count',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Compraram',
          value: 'metrics.clients_metrics.received_and_bought.count',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Compraram produtos divulgados',
          value: 'metrics.clients_metrics.received_with_products.count',
          width: '178px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Taxa de entrega',
          value: 'delivery_rate_metrics',
          width: '178px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Investimento',
          value: 'metrics.analytic_metrics.price',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Créditos utilizados',
          value: 'metrics.analytic_metrics.credits',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Taxa de conversão',
          value: 'conversion_rate_metrics',
          width: '178px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Disparos',
          value: 'metrics.general_metrics.general.sent.messages',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Canais',
          value: 'mode',
          width: '178px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Faturamento total',
          value: 'metrics.clients_metrics.received_and_bought.revenue',
          width: '188px',
          isRequired: false,
          isDefault: true,
          sortable: false,
        },
        {
          text: 'Ticket médio',
          value: 'metrics.clients_metrics.received_and_bought.average_ticket',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Alavancagem',
          value: 'metrics.analytic_metrics.received_roi',
          width: '160px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Faturamento da cesta nas conversões com produtos da campanha',
          value: 'metrics.clients_metrics.received_with_products.revenue',
          width: '200px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Faturamento dos produtos divulgados nas conversões da campanha',
          value: 'metrics.products_metrics.totals.received.revenue',
          width: '200px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Quantidade vendida dos produtos divulgados nas conversões da campanha',
          value: 'metrics.products_metrics.totals.received.quantity',
          width: '200px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Impacto da campanha nas vendas dos produtos divulgados',
          value: 'metrics.products_metrics.totals.received.impact',
          width: '200px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Compraram apenas produtos divulgados',
          value: 'metrics.clients_metrics.received_with_only_products.count',
          width: '178px',
          isRequired: false,
          sortable: false,
        },
        {
          text: 'Abertos',
          value: 'metrics.general_metrics.general.read.messages',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Cliques no botão',
          value: 'metrics.general_metrics.general.clicked.messages',
          width: '188px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Disparadores',
          value: 'metrics.general_metrics.general.senders_quantity.messages',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Desistências',
          value: 'metrics.contacts_metrics.blacklisted_quantity',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Produtos',
          value: 'products',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '150px',
          isRequired: false,
          isDefault: false,
          sortable: false,
        },
      ]
      if (this.$getAllowedModules()['products_tree']) {
        headers.push(
          ...[
            {
              text: 'Categorias Nível 1',
              value: 'categories_level_1',
              width: '150px',
              isRequired: false,
              isDefault: false,
              sortable: false,
            },
            {
              text: 'Categorias Nível 2',
              value: 'categories_level_2',
              width: '150px',
              isRequired: false,
              isDefault: false,
              sortable: false,
            },
            {
              text: 'Categorias Nível 3',
              value: 'categories_level_3',
              width: '150px',
              isRequired: false,
              isDefault: false,
              sortable: false,
            },
          ]
        )
      }
      return headers
    },
  },

  methods: {
    criteriaGroup,
    campaignReceivedCriteria,
    campaignReceivedAndPurchasedCriteria,
    campaignReceivedAndPurchasedAnyProductCriteria,
    campaignReceivedAndOnlyPurchasedAnyProductCriteria,
    getCategories(products, level) {
      return [...new Set(products?.map((product) => product?.erp_categories?.[level]?.name))].filter(Boolean)
    },
    getCampaignTypeLabel(campaignType) {
      const campaignTypes = {
        SINGLE_CHANNEL: 'Direta',
        OMNI_CHANNEL: 'Mar.ia',
      }

      return campaignTypes?.[campaignType]
    },
    isMariaCampaign(campaign) {
      return campaign.campaign_type === 'OMNI_CHANNEL'
    },
    checkIfSomeCampaignModesHasCosts(mode) {
      const modes = Array.isArray(mode) ? mode : []
      const hasModesWithCosts = modes.some((mode) => mode === 'SMS' || mode === 'EMAIL')
      const isAllModes = modes.length === 0
      return hasModesWithCosts || isAllModes
    },
    formatDate(date) {
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy')
    },
    getCampaingsLength(campaign) {
      return this.getMariaCampaignChannels(campaign).length
    },
    getMariaCampaignChannels(campaign) {
      const campaignChannels = Object.entries(campaign?.messages_dispatcher?.budget?.channels || {})
        .filter(([_, value]) => value > 0)
        .map(([key]) => this.getCampaignModeDescription(key.toUpperCase()))
      return campaignChannels
    },
    getFormatedMariaCampaignChannels(campaign) {
      return this.getMariaCampaignChannels(campaign)
        .map((channel) => channel)
        .join(', ')
    },
    getCampaignModeDescription(mode) {
      const modesDescription = {
        WHATSAPP: 'WhatsApp',
        SMS: 'SMS',
        PUSH: 'Aplicativo',
        EMAIL: 'E-mail',
      }

      return modesDescription?.[mode]
    },
    getRateMetrics(firstNumber, secondNumber) {
      if (secondNumber === 0) return 0
      const rateDivision = firstNumber / secondNumber
      const rateMetrics = rateDivision * 100 || 0

      return rateMetrics
    },
    getCampaignRouteLink(_id, mode, isMariaCampaign) {
      if (isMariaCampaign) return `/maria/campanha-da-maria/resultados/${_id}`

      const modesDescription = {
        WHATSAPP: 'whatsapp',
        SMS: 'sms',
        PUSH: 'aplicativo',
        EMAIL: 'email',
      }

      return `/campanhas/${modesDescription?.[mode]}/resultados/${_id}`
    },
    getCsvDataShape(item) {
      const productsAveragePrice = item.metrics.products_metrics.table.reduce((acc, item) => {
        if (item.product?.sku) {
          return {
            ...acc,
            [item.product.sku]: roundFloat(item.received?.quantity > 0 ? item.received?.revenue / item.received?.quantity : 0, 2),
          }
        }
        return acc
      }, {})
      return {
        title: item?.title,
        description: item?.description,
        final_date: `${this.formatDate(item?.initial_date)} - ${this.formatDate(item?.final_date)}`,
        campaign_type: this.getCampaignTypeLabel(item.campaign_type),
        'metrics.clients_metrics.received.count': item.metrics.clients_metrics.received.count,
        'metrics.clients_metrics.received_and_bought.count': item.metrics.clients_metrics.received_and_bought.count,
        'metrics.clients_metrics.received_with_products.count': item.metrics.clients_metrics.received_with_products.count,
        delivery_rate_metrics: roundFloat(
          item.metrics.general_metrics.general.sent.messages === 0
            ? 0
            : item.metrics.general_metrics.general.received.messages / item.metrics.general_metrics.general.sent.messages,
          4
        ),
        'metrics.analytic_metrics.price': roundFloat(item.metrics.analytic_metrics.price, 2),
        'metrics.analytic_metrics.credits': roundFloat(item.metrics.analytic_metrics.credits, 0),
        conversion_rate_metrics: roundFloat(
          item.metrics.clients_metrics.received.count === 0
            ? 0
            : item.metrics.clients_metrics.received_and_bought.count / item.metrics.clients_metrics.received.count,
          4
        ),
        'metrics.general_metrics.general.sent.messages': roundFloat(item.metrics.general_metrics.general.sent.messages, 0),
        ...(this.isMariaCampaign(item) ? { mode: this.getFormatedMariaCampaignChannels(item) } : { mode: this.getCampaignModeDescription(item.mode) }),
        'metrics.clients_metrics.received_and_bought.revenue': roundFloat(item.metrics.clients_metrics.received_and_bought.revenue, 2),
        'metrics.clients_metrics.received_and_bought.average_ticket': roundFloat(item.metrics.clients_metrics.received_and_bought.average_ticket, 2),
        'metrics.analytic_metrics.received_roi': roundFloat(item.metrics.analytic_metrics.received_roi, 0),
        'metrics.clients_metrics.received_with_products.revenue': roundFloat(item.metrics.clients_metrics.received_with_products.revenue, 2),
        'metrics.products_metrics.totals.received.revenue': roundFloat(item.metrics.products_metrics.totals.received.revenue, 2),
        'metrics.products_metrics.totals.received.quantity': roundFloat(item.metrics.products_metrics.totals.received.quantity, 2),
        'metrics.products_metrics.totals.received.impact': roundFloat(
          item.metrics.products_metrics.totals.received.quantity
            ? item.metrics.products_metrics.totals.received.quantity / item.metrics.products_metrics.totals.total.quantity
            : 0,
          2
        ),
        'metrics.clients_metrics.received_with_only_products.count': item.metrics.clients_metrics.received_with_only_products.count,
        'metrics.general_metrics.general.segments_quantity.messages': item.metrics.general_metrics.general.segments_quantity.messages,
        'metrics.general_metrics.general.read.messages': item.metrics.general_metrics.general.read.messages,
        'metrics.general_metrics.general.clicked.messages': item.metrics.general_metrics.general.clicked.messages,
        'metrics.general_metrics.general.senders_quantity.messages': item.metrics.general_metrics.general.senders_quantity.messages,
        'metrics.contacts_metrics.blacklisted_quantity': item.metrics.contacts_metrics.blacklisted_quantity,
        products: truncate(
          item.products
            ?.map((product) => (product ? `${product.sku || ''} - ${product.name || ''} - ${productsAveragePrice?.[product.sku] ?? ''}` : ''))
            .filter(Boolean)
            .join(' - '),
          {
            length: 32700,
            separator: ' - ',
          }
        ),
        units: item.units
          ?.map((unit) => (unit ? `${unit.id_erp || ''} - ${unit.name || ''}` : ''))
          .filter(Boolean)
          .join(', '),
        categories_level_1: this.getCategories(item.products, 1).join(', '),
        categories_level_2: this.getCategories(item.products, 2).join(', '),
        categories_level_3: this.getCategories(item.products, 3).join(', '),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dash-campanhas-title {
  font-size: 26px;
  color: $cinza-d10 !important;
  font-weight: bold;
}

::v-deep .v-data-table__empty-wrapper {
  color: $cinza-l50 !important;
}
</style>
